import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Plus, Search, Filter, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import PalletEditor, { EMPTY_PALLET, cleanPallets, totalPallets } from "@/components/tms/PalletEditor";
import StopFields from "@/components/tms/StopFields";
import MarginSummary from "@/components/tms/MarginSummary";

const STATUSES = ["En attente", "En cours", "Livré", "Litige"];
const STATUS_CLS = {
    "En attente": "chip-attente",
    "En cours": "chip-cours",
    "Livré": "chip-livre",
    "Litige": "chip-litige",
};

const EMPTY_OT = {
    client_name: "", client_contact: "", client_order_ref: "", goods_description: "",
    weight_kg: "", volume_m3: "", pallets: [{ ...EMPTY_PALLET }],
    pickup_address: "", pickup_city: "", pickup_country: "France", pickup_contact_name: "", pickup_contact_phone: "",
    delivery_address: "", delivery_city: "", delivery_country: "France", delivery_contact_name: "", delivery_contact_phone: "",
    carrier_id: "", pickup_date: "", delivery_date: "",
    status: "En attente", notes: "", price_ht: "", purchase_price_ht: "",
};

export default function OtListPage() {
    const [ots, setOts] = useState([]);
    const [carriers, setCarriers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: "", carrier_id: "", q: "", date_from: "", date_to: "" });
    const [modal, setModal] = useState(false);
    const [form, setForm] = useState(EMPTY_OT);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
            const [{ data: list }, { data: cs }] = await Promise.all([
                api.get("/ot", { params }),
                api.get("/carriers"),
            ]);
            setOts(list);
            setCarriers(cs);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

    const carrierMap = useMemo(() => Object.fromEntries(carriers.map((c) => [c.id, c])), [carriers]);

    const create = async (e) => {
        e.preventDefault();
        if (totalPallets(form.pallets) < 1) {
            toast.error("Indiquez au moins une palette");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                weight_kg: Number(form.weight_kg) || 0,
                volume_m3: Number(form.volume_m3) || 0,
                price_ht: Number(form.price_ht) || 0,
                purchase_price_ht: Number(form.purchase_price_ht) || 0,
                pallets: cleanPallets(form.pallets),
                carrier_id: form.carrier_id || null,
                pickup_date: form.pickup_date || null,
                delivery_date: form.delivery_date || null,
            };
            await api.post("/ot", payload);
            toast.success("OT créé");
            setModal(false);
            setForm(EMPTY_OT);
            await load();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 lg:p-10 pb-24 lg:pb-10 max-w-7xl mx-auto">
            <header className="flex flex-wrap items-end justify-between gap-5">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-semibold">OT · Gestion</div>
                    <h1 className="font-display text-2xl lg:text-3xl font-bold text-white mt-2">Ordres de transport</h1>
                    <p className="text-sm text-slate-400 mt-1">Création, suivi et mise à jour des statuts.</p>
                </div>
                <button
                    data-testid="tms-create-ot-button"
                    onClick={() => { setForm(EMPTY_OT); setModal(true); }}
                    className="kz-btn-primary text-sm"
                >
                    <Plus size={16} /> Nouvel OT
                </button>
            </header>

            {/* filters */}
            <div className="mt-6 flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                    <input
                        data-testid="tms-ot-search-input"
                        value={filters.q}
                        onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                        placeholder="Référence, client, ville…"
                        className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#0f172a] border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-400"
                    />
                </div>
                <select
                    data-testid="tms-ot-filter-status"
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    className="py-2.5 px-3 rounded-xl bg-[#0f172a] border border-slate-700 text-sm text-white"
                >
                    <option value="">Tous statuts</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                    data-testid="tms-ot-filter-carrier"
                    value={filters.carrier_id}
                    onChange={(e) => setFilters((f) => ({ ...f, carrier_id: e.target.value }))}
                    className="py-2.5 px-3 rounded-xl bg-[#0f172a] border border-slate-700 text-sm text-white"
                >
                    <option value="">Tous transporteurs</option>
                    {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                    className="py-2.5 px-3 rounded-xl bg-[#0f172a] border border-slate-700 text-sm text-white"
                />
                <span className="text-slate-500 text-sm">→</span>
                <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                    className="py-2.5 px-3 rounded-xl bg-[#0f172a] border border-slate-700 text-sm text-white"
                />
                {(filters.status || filters.carrier_id || filters.q || filters.date_from || filters.date_to) && (
                    <button onClick={() => setFilters({ status: "", carrier_id: "", q: "", date_from: "", date_to: "" })}
                        className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                    >
                        <X size={12} /> Réinitialiser
                    </button>
                )}
            </div>

            {/* table */}
            <div className="mt-6 rounded-2xl border border-slate-800 bg-[#0f172a] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[900px]">
                        <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3 font-medium">Référence</th>
                                <th className="text-left px-5 py-3 font-medium">Client</th>
                                <th className="text-left px-5 py-3 font-medium">Marchandise</th>
                                <th className="text-left px-5 py-3 font-medium">Trajet</th>
                                <th className="text-left px-5 py-3 font-medium">Transporteur</th>
                                <th className="text-left px-5 py-3 font-medium">Enlèvement</th>
                                <th className="text-left px-5 py-3 font-medium">Statut</th>
                                <th className="text-right px-5 py-3 font-medium">Prix HT</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {loading && (
                                <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" />Chargement…</td></tr>
                            )}
                            {!loading && ots.map((o) => (
                                <tr key={o.id} data-testid={`ot-row-${o.reference}`} className="hover:bg-slate-800/40">
                                    <td className="px-5 py-3 font-mono">
                                        <Link to={`/tms/ot/${o.id}`} className="text-slate-100 hover:text-orange-300">{o.reference}</Link>
                                    </td>
                                    <td className="px-5 py-3 text-slate-200">{o.client_name}</td>
                                    <td className="px-5 py-3 text-slate-400 max-w-[220px] truncate" title={o.goods_description}>{o.goods_description}</td>
                                    <td className="px-5 py-3 text-slate-400">{o.pickup_city} → {o.delivery_city}</td>
                                    <td className="px-5 py-3 text-slate-400">{carrierMap[o.carrier_id]?.name || "—"}</td>
                                    <td className="px-5 py-3 text-slate-400 font-mono text-xs">{o.pickup_date || "—"}</td>
                                    <td className="px-5 py-3"><span className={`chip ${STATUS_CLS[o.status]}`}>{o.status}</span></td>
                                    <td className="px-5 py-3 text-right text-slate-200 font-mono">{(o.price_ht || 0).toLocaleString("fr-FR")} €</td>
                                </tr>
                            ))}
                            {!loading && ots.length === 0 && (
                                <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">Aucun OT ne correspond aux filtres.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* modal create */}
            {modal && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-5 overflow-y-auto">
                    <div className="bg-[#0f172a] rounded-2xl border border-slate-800 w-full max-w-3xl mt-8">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
                            <h3 className="font-display font-semibold text-white">Nouvel ordre de transport</h3>
                            <button onClick={() => setModal(false)} className="text-slate-400 hover:text-white"><X size={18} /></button>
                        </div>
                        <form onSubmit={create} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <F label="Client *" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} required testid="ot-form-client" />
                            <F label="Contact client" value={form.client_contact} onChange={(v) => setForm({ ...form, client_contact: v })} />
                            <F label="Réf. commande client" value={form.client_order_ref} onChange={(v) => setForm({ ...form, client_order_ref: v })} testid="ot-form-order-ref" />
                            <div className="hidden md:block" />
                            <F label="Description marchandise *" className="md:col-span-2" value={form.goods_description} onChange={(v) => setForm({ ...form, goods_description: v })} required testid="ot-form-goods" />
                            <div className="md:col-span-2">
                                <label className="text-xs text-slate-400 mb-1.5 block">Palettes *</label>
                                <PalletEditor pallets={form.pallets} onChange={(p) => setForm({ ...form, pallets: p })} />
                            </div>
                            <F label="Poids (kg)" type="number" value={form.weight_kg} onChange={(v) => setForm({ ...form, weight_kg: v })} />
                            <F label="Volume (m³)" type="number" value={form.volume_m3} onChange={(v) => setForm({ ...form, volume_m3: v })} />
                            <StopFields prefix="pickup" title="Enlèvement" value={form} onChange={setForm} required />
                            <StopFields prefix="delivery" title="Destination" value={form} onChange={setForm} required />
                            <F label="Date enlèvement" type="date" value={form.pickup_date} onChange={(v) => setForm({ ...form, pickup_date: v })} />
                            <F label="Date livraison prévue" type="date" value={form.delivery_date} onChange={(v) => setForm({ ...form, delivery_date: v })} />
                            <div>
                                <label className="text-xs text-slate-400 mb-1.5 block">Transporteur</label>
                                <select value={form.carrier_id} onChange={(e) => setForm({ ...form, carrier_id: e.target.value })}
                                    className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white">
                                    <option value="">— Non assigné —</option>
                                    {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 mb-1.5 block">Statut initial</label>
                                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white">
                                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <F label="Prix d'achat HT (€) — transporteur" type="number" value={form.purchase_price_ht} onChange={(v) => setForm({ ...form, purchase_price_ht: v })} testid="ot-form-purchase" />
                            <F label="Prix de vente HT (€) — client" type="number" value={form.price_ht} onChange={(v) => setForm({ ...form, price_ht: v })} testid="ot-form-sale" />
                            <div className="md:col-span-2"><MarginSummary sale={form.price_ht} purchase={form.purchase_price_ht} testid="ot-form-margin" /></div>
                            <div className="md:col-span-2">
                                <label className="text-xs text-slate-400 mb-1.5 block">Notes</label>
                                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white" />
                            </div>
                            <div className="md:col-span-2 flex justify-end gap-3 pt-2 border-t border-slate-800 mt-2">
                                <button type="button" onClick={() => setModal(false)} className="kz-btn-ghost text-sm">Annuler</button>
                                <button type="submit" disabled={saving} data-testid="ot-form-submit" className="kz-btn-primary text-sm">
                                    {saving && <Loader2 className="animate-spin" size={14} />} Créer l'OT
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

function F({ label, value, onChange, type = "text", required, className = "", testid }) {
    return (
        <div className={className}>
            <label className="text-xs text-slate-400 mb-1.5 block">{label}</label>
            <input
                data-testid={testid}
                type={type}
                step={type === "number" ? "any" : undefined}
                value={value}
                required={required}
                onChange={(e) => onChange(e.target.value)}
                className="w-full py-2.5 px-3 rounded-xl bg-[#020617] border border-slate-700 text-sm text-white placeholder:text-slate-600"
            />
        </div>
    );
}
